class ProductController {
  constructor() {
    // Gallery must init even when ATC form is missing (e.g. info mode)
    this.initializeAllSwipers();
    this.initializeVariables();
    this.setupEventListeners();
    this.setupSellingPlanListeners();
    this.setInitialVariantUrl();
  }

  initializeVariables() {
    // Price elements
    this.priceElement = document.querySelector(".price-actual");
    this.compareAtPrice = document.querySelector(".price-compare");
    this.basePrice = this.getPriceValue(this.priceElement);

    // In initializeVariables()
    this.purchaseOptions = document.querySelectorAll(
      'input[name="purchaseOption"]'
    );
    this.onetimeRadio = document.querySelector("#rc-radio1");
    this.subscriptionRadio = document.querySelector("#rc-radio2");
    this.sellingPlanText = document.querySelector(".rc-selling-plans-single");

    // Variant elements
    this.variantWrapper = document.querySelector("[data-variant-wrapper]");
    this.optionSelectors = document.querySelectorAll("[data-option-selector]");

    // Form elements
    this.form = document.querySelector(`form[data-product-form]`);
    this.addToCartButton = document.querySelector("[data-add-to-cart]");
    this.variantIdInput = document.querySelector("[data-variant-id]");
    this.sellingPlanInput = this.form?.querySelector('[name="selling_plan"]');
    this.loadingSpinner = document.querySelector(".loading-overlay__spinner");

    // Selling plan elements
    this.purchaseOptions = document.querySelectorAll(
      "[data-selling-plan-group]"
    );
    this.sellingPlanSelect = document.querySelector(
      "[data-selling-plan-select]"
    );

    // Quantity elements
    this.quantityInput = document.querySelector("[data-quantity-input]");
    this.quantityPlus = document.querySelector("[data-quantity-plus]");
    this.quantityMinus = document.querySelector("[data-quantity-minus]");

    // Cart notification
    this.cartNotification = this.createCartNotification();
    document.body.appendChild(this.cartNotification);

    // Get variants data
    this.variants = window.productVariants || [];

    // Check URL for variant ID
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get("variant");

    // Initial variant setup
    this.updateAllVariantSelectors();

    // Ensure the correct variant is selected (skip when form is absent, e.g. info mode)
    if (this.variantIdInput) {
      if (variantIdFromUrl) {
        const variantFromUrl = this.variants.find(v => v.id.toString() === variantIdFromUrl);
        if (variantFromUrl) {
          this.variantIdInput.value = variantFromUrl.id;
          this.basePrice = variantFromUrl.price / 100;
          this.updatePrice(1);
          this.updateAddToCartButton(variantFromUrl.available);
        }
      } else if (!this.variantIdInput.value && this.variants.length > 0) {
        const firstVariant = this.variants[0];
        this.variantIdInput.value = firstVariant.id;
        this.basePrice = firstVariant.price / 100;
        this.updatePrice(1);
        this.updateAddToCartButton(firstVariant.available);
      }
    }

    // Cart state
    this.isAddingToCart = false;
  }

  getPriceValue(element) {
    if (!element) return 0;
    return parseFloat(element.textContent.replace(/[^0-9.]/g, "")) || 0;
  }

  createCartNotification() {
    const notification = document.createElement("div");
    notification.className = "cart-notification";
    notification.style.cssText = `
      position: fixed;
      top: 0px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 16px 24px;
      border-radius: 4px;
      z-index: 1000;
      display: none;
      opacity: 0;
      transition: opacity 0.3s ease;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;
    return notification;
  }

  setupEventListeners() {
    // Handle form submission
    if (this.form) {
      this.form.addEventListener("submit", this.handleFormSubmit.bind(this));
    }

    // Variant selection
    this.optionSelectors?.forEach((selector) => {
      if (selector.tagName === "SELECT") {
        selector.addEventListener("change", () => this.handleVariantChange());
      } else {
        const pills = selector.querySelectorAll('input[type="radio"]');
        pills.forEach((radio) => {
          radio.addEventListener("change", () => this.handleVariantChange());
        });
      }
    });

    // Update quantity control listeners
    if (this.quantityPlus && this.quantityInput) {
      this.quantityPlus.addEventListener('click', (e) => {
        e.preventDefault();
        const currentValue = parseInt(this.quantityInput.value) || 1;
        this.quantityInput.value = currentValue + 1;
        this.handleQuantityChange();
      });
    }

    if (this.quantityMinus && this.quantityInput) {
      this.quantityMinus.addEventListener('click', (e) => {
        e.preventDefault();
        const currentValue = parseInt(this.quantityInput.value) || 1;
        if (currentValue > 1) {
          this.quantityInput.value = currentValue - 1;
          this.handleQuantityChange();
        }
      });
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener("change", () => this.handleQuantityChange());
      this.quantityInput.addEventListener("input", () => this.handleQuantityChange());
    }

    // Listen for cart update events
    document.addEventListener("cart:update", this.updateCartUI.bind(this));
  }

  setupSellingPlanListeners() {
    // Clear any existing listeners first
    this.subscriptionRadio?.removeEventListener(
      "change",
      this.handleSubscriptionChange
    );
    this.onetimeRadio?.removeEventListener("change", this.handleOnetimeChange);

    // Add listeners to radio buttons
    const purchaseOptions = document.querySelectorAll(
      'input[name^="purchaseOption_template"]'
    );

    purchaseOptions.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        if (e.target.dataset.radioType === "selling_plan") {
          this.handleSubscriptionChange(e);
        } else {
          this.handleOnetimeChange(e);
        }
      });
    });

    // Set initial state
    this.handleInitialState();
  }

  handleSubscriptionChange(e) {
    if (!e.target.checked) return;

    const sellingPlanId = e.target.dataset.sellingPlanId;
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = sellingPlanId;
      console.log("Set selling plan ID:", this.sellingPlanInput.value);
    } else {
      console.warn("Selling plan input not found");
    }

    // Reset quantity to 1
    const formIdPart = this.form?.id?.split('-')[1];
    const quantityInput = formIdPart
      ? document.querySelector('#Quantity-' + formIdPart)
      : null;
    if (quantityInput) {
      quantityInput.value = 1;
      // Trigger change event to ensure UI updates
      const event = new Event('change', { bubbles: true });
      quantityInput.dispatchEvent(event);
    }

    // Update price to subscription price
    this.updatePrice(1);
  }

  handleOnetimeChange(e) {
    if (!e.target.checked) return;

    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = "";
      console.log("Cleared selling plan ID");
    } else {
      console.warn("Selling plan input not found");
    }

    // Reset quantity to 1
    const formIdPart = this.form?.id?.split('-')[1];
    const quantityInput = formIdPart
      ? document.querySelector('#Quantity-' + formIdPart)
      : null;
    if (quantityInput) {
      quantityInput.value = 1;
      // Trigger change event to ensure UI updates
      const event = new Event('change', { bubbles: true });
      quantityInput.dispatchEvent(event);
    }

    // Update price to regular price
    this.updatePrice(1);
  }

  handleFormSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    if (this.isAddingToCart) return;

    this.handleAddToCart();
    return false;
  }

  async handleAddToCart() {
    if (!this.form || this.isAddingToCart) return;

    const quantity = parseInt(this.quantityInput?.value) || 1;
    const variantId = this.variantIdInput?.value;
    const sellingPlanId = this.sellingPlanInput?.value;

    if (!variantId) {
      this.showCartNotification("Please select a variant", "error");
      return;
    }

    // Store original button text and width
    const textSpan = this.addToCartButton.querySelector(".add-to-cart-text");
    const originalText = textSpan?.textContent || "";
    const originalWidth = this.addToCartButton.offsetWidth;

    this.isAddingToCart = true;
    this.addToCartButton.disabled = true;
    this.addToCartButton.style.width = `${originalWidth}px`; // Lock width
    if (textSpan) textSpan.textContent = "Adding to Cart...";
    this.loadingSpinner?.classList.remove("hidden");

    try {
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", quantity);
      if (sellingPlanId) {
        formData.append("selling_plan", sellingPlanId);
      }

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      // Update cart count and trigger cart update event
      this.updateCart();

      // Show success notification
      this.showCartNotification("Item added to cart successfully");

      // Update button text
      if (textSpan) textSpan.textContent = "Added to Cart!";

      // Trigger custom event
      document.dispatchEvent(
        new CustomEvent("cart:added", {
          detail: {
            variant: data,
            quantity: quantity,
            selling_plan: sellingPlanId,
          },
        })
      );
    } catch (error) {
      console.error("Error adding to cart:", error);
      this.showCartNotification(
        "There was an error adding to cart. Please try again.",
        "error"
      );
      // Reset button text on error
      if (textSpan) textSpan.textContent = originalText;
    } finally {
      this.loadingSpinner?.classList.add("hidden");

      // Reset button after 2 seconds
      setTimeout(() => {
        this.isAddingToCart = false;
        this.addToCartButton.disabled = false;
        this.addToCartButton.style.width = ""; // Remove fixed width
        if (textSpan) textSpan.textContent = originalText;
      }, 2000);
    }
  }

  async updateCart() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch cart");

      const cart = await response.json();

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          detail: { cart },
        })
      );
    } catch (error) {
      console.error("Error updating cart:", error);
    }
  }

  updateCartUI(event) {
    const cart = event.detail.cart;

    // Update any cart count elements
    const cartCountElements = document.querySelectorAll("[data-cart-count]");
    cartCountElements.forEach((element) => {
      element.textContent = cart.item_count;
      element.classList.toggle("hidden", cart.item_count === 0);
    });

    this.quantityInput.value = 1;
    // Update items-counter span
    const itemsCounter = document.querySelector(".items-counter");
    if (itemsCounter) {
      itemsCounter.innerHTML = cart.item_count;
    }
  }

  showCartNotification(message, type = "success") {
    if (!this.cartNotification) return;

    this.cartNotification.textContent = message;
    this.cartNotification.style.background =
      type === "success" ? "#4CAF50" : "#f44336";
    this.cartNotification.style.display = "block";

    // Force reflow
    this.cartNotification.offsetHeight;

    this.cartNotification.style.opacity = "1";

    setTimeout(() => {
      this.cartNotification.style.opacity = "0";
      setTimeout(() => {
        this.cartNotification.style.display = "none";
      }, 300);
    }, 3000);
  }

  handleQuantityChange() {
    if (!this.quantityInput) return;
    
    let value = parseInt(this.quantityInput.value);
    if (isNaN(value) || value < 1) value = 1;
    
    // Ensure we're using the step value of 1
    value = Math.round(value);
    
    this.quantityInput.value = value;
    this.updatePrice(value);
  }

  updatePrice(quantity) {
    if (!this.priceElement) return;

    let price = this.basePrice;
    const subscriptionRadio = document.querySelector('input[data-radio-type="selling_plan"]:checked');
    
    // Apply 20% discount for subscription
    if (subscriptionRadio) {
      price = price * 0.8; // 20% discount
    }

    const total = price * quantity;
    const priceSpan = this.priceElement.querySelector("span") || this.priceElement;
    priceSpan.textContent = this.formatMoney(total);

    if (this.compareAtPrice) {
      const comparePrice = this.getPriceValue(this.compareAtPrice);
      const comparePriceSpan = this.compareAtPrice.querySelector("span") || this.compareAtPrice;
      comparePriceSpan.textContent = this.formatMoney(comparePrice * quantity);
    }
  }

  formatMoney(amount) {
    return `$${amount.toFixed(2)} CAD`;
  }

  updateAllVariantSelectors() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");

    if (!variantId || !this.variants) return;

    const variant = this.variants.find((v) => v.id.toString() === variantId);
    if (!variant) return;

    variant.options.forEach((optionValue, index) => {
      const selector = this.optionSelectors[index];
      if (!selector) return;

      if (selector.tagName === "SELECT") {
        selector.value = optionValue;
      } else {
        const radio = selector.querySelector(`input[value="${optionValue}"]`);
        if (radio) {
          radio.checked = true;
          radio.closest(".variant-pill")?.classList.add("active");
        }
      }
    });
  }

  handleVariantChange() {
    if (!this.optionSelectors || !this.variants) return;

    const selectedOptions = Array.from(this.optionSelectors).map((selector) => {
      if (selector.tagName === "SELECT") {
        return selector.value;
      } else {
        const checkedRadio = selector.querySelector("input:checked");
        return checkedRadio ? checkedRadio.value : null;
      }
    });

    const selectedVariant = this.variants.find((variant) =>
      variant.options.every(
        (option, index) => option === selectedOptions[index]
      )
    );

    if (!selectedVariant) return;

    // Update form ID
    if (this.variantIdInput) {
      this.variantIdInput.value = selectedVariant.id;
    }

    // Update price
    this.basePrice = selectedVariant.price / 100;
    this.updatePrice(parseInt(this.quantityInput?.value) || 1);

    // reset quantity selection
    this.quantityInput.value = 1;
    // reset price
    this.updatePrice(1);
    // Update URL
    if (history.replaceState) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("variant", selectedVariant.id);
      window.history.replaceState({ path: newUrl.href }, "", newUrl.href);
    }

    // Update variant pills styling
    this.updatePillsStyle();

    // Update variant image
    this.updateVariantImage(selectedVariant);

    // Update button state
    this.updateAddToCartButton(selectedVariant.available);
  }

  updateAddToCartButton(available) {
    if (!this.addToCartButton) return;

    this.addToCartButton.disabled = !available;
    const textSpan = this.addToCartButton.querySelector(".add-to-cart-text");
    if (textSpan && !this.isAddingToCart) {
      textSpan.textContent = available ? "Add to Cart" : "Sold Out";
    }
  }

  updatePillsStyle() {
    this.optionSelectors?.forEach((selector) => {
      if (selector.classList.contains("variant-pills")) {
        selector.querySelectorAll(".variant-pill").forEach((pill) => {
          const radio = pill.querySelector('input[type="radio"]');
          if (radio) {
            pill.classList.toggle("active", radio.checked);
          }
        });
      }
    });
  }

  updateVariantImage(variant) {
    if (!variant?.featured_image?.src) return;

    document.querySelectorAll(".product-swiper").forEach((swiperElement) => {
      const slides = swiperElement.querySelectorAll(
        ".swiper-slide[data-variant-ids]"
      );

      const variantSlide = Array.from(slides).find((slide) => {
        const variantIds = slide.dataset.variantIds.split(",");
        return variantIds.includes(variant.id.toString());
      });

      if (variantSlide && swiperElement.swiper) {
        const slideIndex = Array.from(variantSlide.parentNode.children).indexOf(
          variantSlide
        );
        swiperElement.swiper.slideTo(slideIndex);
      }
    });
  }

  initializeAllSwipers() {
    if (typeof Swiper === "undefined") {
      console.warn("Swiper is not loaded");
      return;
    }

    // Match gallery visibility breakpoint (desktop vs mobile blocks)
    const isDesktopViewport = window.innerWidth >= 1024;

    document.querySelectorAll(".product-gallery").forEach((galleryContainer) => {
      const visibility =
        galleryContainer.closest("[data-visibility]")?.dataset.visibility ||
        galleryContainer.dataset.visibility;

      if (visibility === "desktop" && !isDesktopViewport) return;
      if (visibility === "mobile" && isDesktopViewport) return;

      this.initializeSingleSwiper(galleryContainer, !isDesktopViewport);
    });

    window.addEventListener("resize", () => {
      const nowDesktop = window.innerWidth >= 1024;
      if (nowDesktop !== isDesktopViewport) {
        location.reload();
      }
    });
  }

  initializeSingleSwiper(galleryContainer, isMobile) {
    const mainElement = galleryContainer.querySelector(".product-swiper");
    if (!mainElement) return;

    // Avoid double-init if section re-renders
    if (mainElement.swiper) {
      mainElement.swiper.destroy(true, true);
    }

    const thumbsElement = galleryContainer.querySelector(".product-thumbs");

    const mainSwiper = new Swiper(mainElement, {
      spaceBetween: 10,
      navigation: {
        nextEl: mainElement.querySelector(".swiper-button-next"),
        prevEl: mainElement.querySelector(".swiper-button-prev"),
      },
      pagination: {
        el: mainElement.querySelector(".swiper-pagination"),
        clickable: true,
        renderBullet: (index, className) => {
          const dotSize = mainElement.dataset.dotSize || "12";
          const dotColor = mainElement.dataset.dotColor || "#FFFFFF";
          return `<span class="${className}" style="
            width: ${dotSize}px;
            height: ${dotSize}px;
            border: 2px solid ${dotColor};
            background: transparent;
            opacity: 1;
          "></span>`;
        },
      },
    });

    mainElement.swiper = mainSwiper;

    const paginationEl = mainElement.querySelector(".swiper-pagination");
    if (paginationEl) {
      paginationEl.style.display = isMobile ? "block" : "none";
    }

    if (!thumbsElement) return;

    // Desktop: show thumbs and wire clicks. Mobile: hide (pagination only).
    if (isMobile) {
      thumbsElement.style.display = "none";
      return;
    }

    thumbsElement.style.display = "block";

    // Destroy previous thumbs instance if any
    if (thumbsElement.swiper) {
      thumbsElement.swiper.destroy(true, true);
    }

    // Thumbs as a simple strip — no freeMode (it eats clicks) and no Thumbs module.
    // Clicks are handled manually via Swiper's click event + native fallback.
    const thumbsSwiper = new Swiper(thumbsElement, {
      spaceBetween: 10,
      slidesPerView: "auto",
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      allowTouchMove: true,
      simulateTouch: true,
    });

    thumbsElement.swiper = thumbsSwiper;

    const goToSlide = (index) => {
      if (index == null || index < 0) return;
      mainSwiper.slideTo(index);
      thumbsElement.querySelectorAll(".swiper-slide").forEach((slide, i) => {
        slide.classList.toggle("swiper-slide-thumb-active", i === index);
      });
    };

    // Swiper's own click/tap (reliable with touch + mouse)
    thumbsSwiper.on("click", (swiper) => {
      goToSlide(swiper.clickedIndex);
    });

    // Native fallback in case Swiper swallows the event
    thumbsElement.addEventListener(
      "click",
      (event) => {
        const slide = event.target.closest(".swiper-slide");
        if (!slide || !thumbsElement.contains(slide)) return;
        const index = Array.from(thumbsElement.querySelectorAll(".swiper-slide")).indexOf(slide);
        goToSlide(index);
      },
      true
    );

    mainSwiper.on("slideChange", () => {
      goToSlide(mainSwiper.activeIndex);
    });

    // Mark first thumb active
    goToSlide(mainSwiper.activeIndex || 0);
  }

  // Update the variant image handler to work with multiple swipers
  updateVariantImage(variant) {
    if (!variant?.featured_image?.src) return;

    document.querySelectorAll(".product-swiper").forEach((swiperElement) => {
      const slides = swiperElement.querySelectorAll(
        ".swiper-slide[data-variant-ids]"
      );

      const variantSlide = Array.from(slides).find((slide) => {
        const variantIds = slide.dataset.variantIds.split(",");
        return variantIds.includes(variant.id.toString());
      });

      if (variantSlide && swiperElement.swiper) {
        const slideIndex = Array.from(variantSlide.parentNode.children).indexOf(
          variantSlide
        );
        swiperElement.swiper.slideTo(slideIndex);
      }
    });
  }

  handleInitialState() {
    const purchaseOptions = document.querySelectorAll('input[name^="purchaseOption_template"]');
    const selectedOption = Array.from(purchaseOptions).find(radio => radio.checked);
    
    if (selectedOption) {
      if (selectedOption.dataset.radioType === "selling_plan") {
        this.handleSubscriptionChange({ target: selectedOption });
      } else {
        this.handleOnetimeChange({ target: selectedOption });
      }
    }
  }

  setInitialVariantUrl() {
    if (!window.location.search.includes('variant') && this.variants.length > 0) {
      const firstVariant = this.variants[0];
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('variant', firstVariant.id);
      window.history.replaceState({ path: newUrl.href }, '', newUrl.href);
    }
  }
}

// Initialize on DOM load
document.addEventListener("DOMContentLoaded", () => {
  new ProductController();
});
